import React, { useState, useEffect } from 'react';
import {
  UserMinus, Buildings, Barcode, IdentificationCard, Info, X, Check, ArrowRight, Trash, ShieldCheck,
  ArrowClockwise,
} from '@phosphor-icons/react';
import {
  getbranchnames, getAgentCodesByBranch, deleteAgentCode, saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const changeType = 'AGENT_CODE_DELETE';

function AgentCodeDelete() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);

  const [branchId, setBranchId] = useState('');
  const [branchName, setBranchName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);

  useEffect(() => {
    getbranchnames().then((res) => setBranches(res || [])).catch(() => setBranches([]));
  }, [formKey]);

  const resetFromBranch = () => {
    setAgents([]);
    setReferralCode('');
    setEmployeeCode('');
  };

  const handleBranchChange = async (id) => {
    setBranchId(id || '');
    resetFromBranch();
    if (!id) {
      setBranchName('');
      return;
    }
    const selected = branches.find((b) => String(b.branchconfigurationid) === String(id));
    setBranchName(selected ? selected.branchName : '');

    try {
      const res = await getAgentCodesByBranch(id);
      setAgents(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setAgents([]);
      showToast('error', 'Load Failed', 'Failed to load agent codes for this branch.');
    }
  };

  const handleAgentChange = (code) => {
    setReferralCode(code || '');
    if (!code) {
      setEmployeeCode('');
      return;
    }
    const selected = agents.find((a) => String(a.referralCode) === String(code));
    setEmployeeCode(selected ? (selected.employeecode || '') : '');
  };

  const branchInvalid = touched && !branchId;
  const agentInvalid = touched && !referralCode;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!branchId && !!referralCode
    && reason.trim().length >= 10 && reason.length <= 200;

  const confirmDelete = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingAudit({
      schemaName: null,
      loginName,
      loginTime: new Date().toISOString(),
      changeType,
      oldData: { referralCode, employeeCode, branchName },
      newData: { status: 'DELETED' },
      reason: reason.trim(),
    });
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
    setPendingAudit(null);
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchId('');
    setBranchName('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setPendingAudit(null);
  };

  const doDelete = async () => {
    if (!pendingAudit) return;

    setShowConfirmModal(false);
    setIsSaving(true);

    try {
      await saveData([pendingAudit]);

      try {
        const result = await deleteAgentCode(referralCode);
        const text = typeof result === 'string' ? result : JSON.stringify(result ?? '');
        if (text.toLowerCase().includes('success')) {
          setIsSaving(false);
          showToast('success', 'Success', 'Audit saved and agent code deleted successfully.', true);
          handleClear();
        } else {
          setIsSaving(false);
          showToast('warn', 'Partial Success', `Audit saved, but delete failed: ${text || 'unknown error'}`, true);
        }
      } catch (err) {
        setIsSaving(false);
        showToast('warn', 'Partial Success', apiErrorMessage(err, 'Audit saved, but delete failed. Please try again.'), true);
      }
    } catch (err) {
      setIsSaving(false);
      showToast('error', 'Audit Save Failed', apiErrorMessage(err, 'Audit save failed. Delete was not performed.'), true);
    }
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="agent-code-delete-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="agent-code-delete-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserMinus size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Agent Administration</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Agent Code Delete</div>
          </div>
        </div>

        {/* SECTION A: Branch & Agent Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Agent</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the branch, then the agent code to delete</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> Branch Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`branch-${formKey}`}
              options={branches}
              getLabel={(o) => o.branchName}
              getValue={(o) => o.branchconfigurationid}
              value={branchId}
              onChange={handleBranchChange}
              placeholder="Select branch…"
              emptyMessage="No branches found"
              error={branchInvalid}
              testId="agent-code-delete-branch-select"
            />
            {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch Name is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Barcode size={13} /> Agent Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`agent-${formKey}`}
              options={agents}
              getLabel={(o) => o.referralCode}
              getValue={(o) => o.referralCode}
              value={referralCode}
              onChange={handleAgentChange}
              placeholder="Select agent code…"
              emptyMessage="Select a branch first"
              disabled={!branchId}
              error={agentInvalid}
              testId="agent-code-delete-agent-select"
            />
            {agentInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Agent Code is required</div>}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
            <IdentificationCard size={13} /> Employee Code
          </label>
          <input
            className="input-field"
            style={{ fontSize: '13px', background: '#F9FAFB' }}
            value={employeeCode || ''}
            readOnly
            placeholder="—"
            data-testid="agent-code-delete-employee-code"
          />
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION B: Justification */}
        <SectionLock locked={!branchId || !referralCode} hint="Select a branch and agent code in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Justification</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for deleting this agent code (max 200 characters)</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Describe the reason for deleting this agent code…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="agent-code-delete-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : reason.trim().length < 10 ? 'Minimum 10 characters' : 'Maximum 200 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="agent-code-delete-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={isSaving}
              data-testid="agent-code-delete-delete-button"
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                background: isSaving ? '#F3A6A6' : '#DC2626',
                border: 'none', borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <Trash size={14} style={{ marginRight: '4px' }} />
              {isSaving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="agent-code-delete-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Agent Code Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to permanently delete this agent code. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Agent Code</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{referralCode}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Status</p>
                  <p style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', margin: 0 }}>Deleted</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{reason.trim()}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="agent-code-delete-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={isSaving}
                data-testid="agent-code-delete-confirm-delete"
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                  background: isSaving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentCodeDelete;
